import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { isValidUrl } from '../../../../../shared/utils/url';
import LabeledMultiTextInput from '../../../generic/Forms/LabeledMultiTextInput';
import { icons } from '../../../generic/Icon';
import TooltipIcon from '../../../generic/TooltipIcon';
import Section from '../../../shared/settings/SettingsPageSection';

import styles from './ExcludedSnippetRepositories.module.less';

const MAX_HIDDEN_SNIPPET_URLS = 100;
const MAX_HIDDEN_SNIPPET_URL_LENGTH = 2048;

export type ExcludedSnippetRepositoriesProps = {
  values: string[];
  onChange: (urls: string[]) => void;
  isDisabled: boolean;
};

/**
 * Lets a user maintain a list of repository URLs whose snippet matches should be hidden.
 * The list is saved with the rest of the issue policies when the page-level Save button is clicked.
 *
 * Entries that aren't valid URLs are rejected before they become chips, so only valid
 * URLs are ever persisted. Rejected entries are surfaced in an inline error below the input.
 *
 * Stored/sent as URLs (generic); surfaced to users as "Repositories".
 */
const ExcludedSnippetRepositories: VoidFunctionComponent<ExcludedSnippetRepositoriesProps> = ({
  values,
  onChange,
  isDisabled,
}) => {
  const [invalidUrls, setInvalidUrls] = useState<string[]>([]);

  return (
    <Section>
      <Section.Left
        heading={(
          <>
            Excluded Snippets Repositories
            <span className={styles.helpIcon}>
              <TooltipIcon
                id="excluded-snippet-repositories-help"
                icon={icons.questionCircle}
                tooltip="Available for Licensing and Security scans only"
                showTooltip
              />
            </span>
          </>
        )}
      >
        <Section.Paragraph>
          Add GitHub repository URLs to prevent snippet matches from those sources.
        </Section.Paragraph>
      </Section.Left>
      <Section.Right>
        <LabeledMultiTextInput
          id="project-settings-hidden-snippet-urls"
          label="URL"
          placeholder="Type a repository URL and hit Enter"
          values={values}
          onChange={onChange}
          isDisabled={isDisabled}
          maxItems={MAX_HIDDEN_SNIPPET_URLS}
          maxLength={MAX_HIDDEN_SNIPPET_URL_LENGTH}
          validate={isValidUrl}
          onInvalidItems={setInvalidUrls}
          size="small"
        />
        {invalidUrls.length > 0 && (
          <p className={styles.error}>
            {invalidUrls.length === 1
              ? `"${invalidUrls[0]}" is not a valid URL.`
              : `${invalidUrls.length} entries were skipped because they are not valid URLs: ${invalidUrls.join(', ')}`}
          </p>
        )}
      </Section.Right>
    </Section>
  );
};

export default ExcludedSnippetRepositories;
